/**
 * React hook for handling deep links
 */

import { useEffect } from 'react';
import { deepLinkHandler, type DeepLinkHandler, type DeepLinkEvent } from '@/services/deeplink/DeepLinkHandler';

/**
 * Hook to register a deep link handler for a specific path pattern
 * @param pathPattern - The path pattern to match (e.g., 'auth/callback', 'signup')
 * @param handler - The handler function to call when the pattern matches
 * @param enabled - Whether the handler should be active (default: true)
 */
export function useDeepLinkHandler(
  pathPattern: string,
  handler: DeepLinkHandler,
  enabled: boolean = true
): void {
  useEffect(() => {
    if (!enabled) return;

    // Register the handler
    deepLinkHandler.registerHandler(pathPattern, handler);

    // Cleanup: unregister the handler when component unmounts or dependencies change
    return () => {
      deepLinkHandler.unregisterHandler(pathPattern, handler);
    };
  }, [pathPattern, handler, enabled]);
}

/**
 * Hook to initialize the deep link handler service
 * Should be called once at the app level
 */
export function useDeepLinkInitialization(): void {
  useEffect(() => {
    deepLinkHandler.initialize().catch(error => {
      console.error('Failed to initialize deep link handler:', error);
    });
  }, []);
}

/**
 * Hook for handling auth callback deep links specifically
 * @param onAuthCallback - Function to call when an auth callback deep link is received
 * @param enabled - Whether the handler should be active (default: true)
 */
export function useAuthCallbackDeepLink(
  onAuthCallback: (event: DeepLinkEvent) => void,
  enabled: boolean = true
): void {
  useDeepLinkHandler('auth/callback', onAuthCallback, enabled);
}
